import { uuid, index, bigint, ExtraConfigColumn } from 'drizzle-orm/pg-core';
import { ColumnBaseConfig, ColumnDataType, sql } from 'drizzle-orm';
import {
  isoTimestamp,
  isoTimestampDate,
} from '@trashlab/api/src/domains/core/schemas/base-template';

// Define base object
const baseFields = () => ({
  id: uuid('id').defaultRandom().primaryKey(),
  createdAtTs: isoTimestamp('createdAtTs', {
    withTimezone: true,
  })
    .default(sql`now()`)
    .notNull(),
  updatedAtTs: isoTimestamp('updatedAtTs', {
    withTimezone: true,
  })
    .default(sql`now()`)
    .notNull(),
  versionNumber: bigint('versionNumber', { mode: 'number' })
    .default(1)
    .notNull(),
  archivedAtTs: isoTimestamp('archivedAtTs', {
    withTimezone: true,
  }),
  versionId: uuid('versionId').defaultRandom().notNull().unique(),
});

// Define base object, Date instead of string
const baseFieldsJSDate = () => ({
  id: uuid('id').defaultRandom().primaryKey(),
  createdAtTs: isoTimestampDate('createdAtTs', {
    withTimezone: true,
  })
    .default(sql`now()`)
    .notNull(),
  updatedAtTs: isoTimestampDate('updatedAtTs', {
    withTimezone: true,
  })
    .default(sql`now()`)
    .notNull(),
  versionNumber: bigint('versionNumber', { mode: 'number' })
    .default(1)
    .notNull(),
  archivedAtTs: isoTimestampDate('archivedAtTs', {
    withTimezone: true,
  }),
  versionId: uuid('versionId').defaultRandom().notNull().unique(),
});

// Define base object with tenant ID
const fieldsWithTenantId = () => ({
  ...baseFields(),
  tenantId: uuid('tenantId').notNull(),
});

// Define base object with tenant ID, Date instead of string
const fieldsWithTenantIdJSDate = () => ({
  ...baseFieldsJSDate(),
  tenantId: uuid('tenantId').notNull(),
});

// Define history object
const historyFields = () => ({
  id: uuid('id'),
  createdAtTs: isoTimestamp('createdAtTs', { withTimezone: true }),
  updatedAtTs: isoTimestamp('updatedAtTs', { withTimezone: true }),
  versionNumber: bigint('versionNumber', { mode: 'number' }),
  archivedAtTs: isoTimestamp('archivedAtTs', {
    withTimezone: true,
  }),
  versionId: uuid('versionId').primaryKey(),
});

const historyFieldsJSDate = () => ({
  id: uuid('id'),
  createdAtTs: isoTimestampDate('createdAtTs', { withTimezone: true }),
  updatedAtTs: isoTimestampDate('updatedAtTs', { withTimezone: true }),
  versionNumber: bigint('versionNumber', { mode: 'number' }),
  archivedAtTs: isoTimestampDate('archivedAtTs', {
    withTimezone: true,
  }),
  versionId: uuid('versionId').primaryKey(),
});

// Define history object with tenant ID
const historyFieldsWithTenantId = () => ({
  ...historyFields(),
  tenantId: uuid('tenantId'),
});

const historyFieldsWithTenantIdJSDate = () => ({
  ...historyFieldsJSDate(),
  tenantId: uuid('tenantId'),
});

type StringColumn = ExtraConfigColumn<ColumnBaseConfig<ColumnDataType, string>>;

interface Indexes {
  id: StringColumn;
  createdAtTs: StringColumn;
  updatedAtTs: StringColumn;
  versionNumber: StringColumn;
  archivedAtTs: StringColumn;
  versionId: StringColumn;
}

interface IndexesWithTenantId extends Indexes {
  tenantId: StringColumn;
}

// Define indexes
const baseIndexes = (tableName: string, table: Indexes) => ({
  createdAtTs: index(`${tableName}_createdAtTs_idx`).on(table.createdAtTs),
  updatedAt: index(`${tableName}_updatedAtTs_idx`).on(table.updatedAtTs),
  updatedAt_desc: index(`${tableName}_updatedAtTs_desc_idx`).on(
    table.updatedAtTs.desc(),
  ),
  versionNumber_idx: index(`${tableName}_versionNumber_idx`).on(
    table.versionNumber,
  ),
});

// Define indexes with tenant ID
const indexesWithTenantId = (
  tableName: string,
  table: IndexesWithTenantId,
) => ({
  ...baseIndexes(tableName, table),
  tenantId: index(`${tableName}_tenantId_idx`).on(table.tenantId),
});

// Define exportable objects
const defaultFields = {
  baseFields,
  fieldsWithTenantId,
};

const defaultFieldsJSDate = {
  baseFields: baseFieldsJSDate,
  fieldsWithTenantId: fieldsWithTenantIdJSDate,
};

const defaultHistoryFields = {
  historyFields,
  historyFieldsWithTenantId,
};

const defaultHistoryFieldsJSDate = {
  historyFields: historyFieldsJSDate,
  historyFieldsWithTenantId: historyFieldsWithTenantIdJSDate,
};

const defaultIndexes = {
  baseIndexes,
  indexesWithTenantId,
};

export {
  defaultFields,
  defaultHistoryFields,
  defaultIndexes,
  defaultFieldsJSDate,
  defaultHistoryFieldsJSDate,
};
